import { CheckCircle2, Clock, AlertTriangle, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell,
} from "recharts";
import { StatCard, SectionHeader, ChartTooltipContent } from "./ReportsShared";

export const TaskHealthSection = ({ task_health }) => {
  const taskPieData = [
    { name: "On Time", value: task_health.completed_on_time, color: "#059669" },
    { name: "Late", value: task_health.completed_late, color: "#D97706" },
    { name: "Overdue", value: task_health.still_overdue, color: "#dc2626" },
    { name: "Pending", value: Math.max(0, task_health.total_tasks - task_health.completed_on_time - task_health.completed_late - task_health.still_overdue), color: "#94a3b8" },
  ].filter((d) => d.value > 0);

  return (
    <section data-testid="section-task-health" id="section-tasks">
      <SectionHeader icon={CheckCircle2} title="Task & Deadline Health" subtitle="Completion rates and common bottlenecks" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatCard icon={CheckCircle2} label="On Time" value={task_health.completed_on_time} color="#059669" />
        <StatCard icon={Clock} label="Completed Late" value={task_health.completed_late} color="#D97706" />
        <StatCard icon={AlertTriangle} label="Still Overdue" value={task_health.still_overdue} color="#dc2626" />
        <StatCard icon={TrendingUp} label="On-Time Rate" value={`${task_health.on_time_rate}%`} color="#2563eb" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="card-surface" data-testid="task-pie-chart">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Task Status Breakdown</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center justify-center pb-4">
            {taskPieData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={taskPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value">
                    {taskPieData.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<ChartTooltipContent />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="py-10 text-sm" style={{ color: "var(--t-text-muted)" }}>No tasks yet</p>
            )}
            <div className="space-y-2 ml-2">
              {taskPieData.map((d) => (
                <div key={d.name} className="flex items-center gap-2 text-xs">
                  <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: d.color }} />
                  <span style={{ color: "var(--t-text)" }}>{d.name}</span>
                  <span className="font-semibold" style={{ color: "var(--t-text)" }}>{d.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        <Card className="card-surface" data-testid="missed-deadlines-chart">
          <CardHeader className="py-3 px-5">
            <CardTitle className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--t-text-muted)" }}>Most Missed Deadline Types</CardTitle>
          </CardHeader>
          <CardContent className="px-2 pb-4">
            {task_health.most_missed_deadlines.length > 0 ? (
              <ResponsiveContainer width="100%" height={Math.max(220, task_health.most_missed_deadlines.length * 36)}>
                <BarChart data={task_health.most_missed_deadlines} layout="vertical" barSize={14} margin={{ left: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--t-border)" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "var(--t-text-muted)" }} allowDecimals={false} />
                  <YAxis dataKey="type" type="category" tick={{ fontSize: 10, fill: "var(--t-text-muted)" }} width={160} interval={0} />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Bar dataKey="count" name="Missed" fill="#dc2626" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center py-10 text-sm" style={{ color: "#059669" }}>No missed deadlines!</p>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};
